import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import {
  Box,
  CardContent,
  Card,
  Button,
  Link,
  Stack,
  Typography,
  IconButton,
  MenuItem,
  Tab,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  TextField
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Visibility as ViewIcon,
  Close as CloseIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  sendInvoiceToSAPForPayment,
  sendLLEPaymentToSAP,
  downloadInvoice,
  getAllInvoicePaymentsWithPaging,
  getInvoicePaymentByGroupId
} from './paymentAPIs';
import { PAYMENT_ROLES, PROD, Stakeholder_Type_Mapping } from '../../common/constants';
import { format } from 'date-fns';
import { ConfirmationPopover } from '../../common/commonPopovers/confirmationPopover';
import { lastFiveDigitsComparator } from '../../common/commonFunction';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { GET_MANUAL_PO_LIST, GET_PO_LIST, UPDATE_PAID_STATUS, DOWNLOAD_DOCUMENT, authKey } from '../../common/URLs';
import apiClient from '../../axios';
import { getLocalStorageItem } from '../../common/localStorageUtils';
import { loaderService } from '../../common/custom-loader/loader-service';
import { toast } from 'react-toastify';
import CustomDataGridToolbar from '../../common/customDataGridToolbar';
import { DownloadIcon } from '../../common/icons';
import { Badge } from 'react-bootstrap';

function InvoicePayment() {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const initialized = useRef(false);

  const [invoicePaymentTableDetails, setInvoicePaymentTableDetails] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionText, setActionText] = useState();
  const [selectedAction, setSelectedAction] = useState("");
  const [sortField, setSortField] = React.useState(null);
  const [sortOrder, setSortOrder] = React.useState(null);
  const [searchText, setSearchText] = React.useState("");
  const [rowCount, setRowCount] = React.useState(0);
  const [stakeholderType, setStakeholderType] = React.useState(state?.stakeholderType ?? "All");
  const [status, setStatus] = React.useState(state?.tabValue ?? "InvoiceReceived");
  const [manualPoList, setManualPoList] = useState([]);
  const [selectedPoNumber, setSelectedPoNumber] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);

  const [dispatchedFromDate, setDispatchedFromDate] = useState(null);
  const [dispatchedToDate, setDispatchedToDate] = useState(null);
  const [characterLimit] = useState(16);
  const [approvalInvoiceDate, setApprovalInvoiceDate] = useState(null);
  const [approvalInvoiceNumber, setApprovalInvoiceNumber] = useState("");

  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [groupInvoices, setGroupInvoices] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);

  const viewdownloadDocument = (documentBlobName, finalfileName, type) => {
    loaderService.showLoader();
    return fetch(DOWNLOAD_DOCUMENT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        AuthKey: authKey,
      },
      body: JSON.stringify({
        "fileName": documentBlobName,
        "containerName": "pit",
        "file": ""
      })
    })
      .then((response) => response.blob())
      .then((blob) => {
        if (type === "View") {
          const fileName = finalfileName || "";
          const splittedName = fileName.split('.');
          const fileExtension = splittedName[splittedName.length - 1]?.toLowerCase();
          let fileType;
          switch (fileExtension) {
            case 'pdf':
              fileType = 'application/pdf';
              break;
            case 'png':
              fileType = 'image/png';
              break;
            case 'jpg':
            case 'jpeg':
              fileType = 'image/jpeg';
              break;
            default:
              fileType = 'application/octet-stream';
          }
          const url = window.URL.createObjectURL(new Blob([blob], { type: fileType }));
          const newWindow = window.open(url, '_blank');
          if (newWindow) newWindow.focus();
        }
        else {
          const a = document.createElement("a");
          a.style.display = "none";
          document.body.appendChild(a);
          const url = window.URL.createObjectURL(blob);
          a.href = url;
          a.download = finalfileName;
          a.click();
          window.URL.revokeObjectURL(url);
        }
        loaderService.hideLoader();
      })
      .catch(function (error) {
        console.log(error);
        loaderService.hideLoader();
      });
  };

  const getActivePoFilter = (currentStatus, currentPo) => {
    return currentStatus === "InvoiceReceived" ? currentPo : "";
  };

  const setDetails = (invoicePayments, count) => {
    setInvoicePaymentTableDetails(invoicePayments);
    setRowCount(count);
  };

  const fetchDataWithFilters = (overrides = {}) => {
    const currentStakeholder = overrides.stakeholderType ?? stakeholderType;
    const currentStatus = overrides.status ?? status;
    const currentSearch = overrides.searchText ?? searchText;
    const currentSortField = overrides.sortField ?? sortField;
    const currentSortOrder = overrides.sortOrder ?? sortOrder;
    const currentPo = overrides.selectedPoNumber !== undefined ? overrides.selectedPoNumber : selectedPoNumber;

    const currentDispatchedFromDate = overrides.dispatchedFromDate !== undefined ? overrides.dispatchedFromDate : dispatchedFromDate;
    const currentDispatchedToDate = overrides.dispatchedToDate !== undefined ? overrides.dispatchedToDate : dispatchedToDate;

    const activePo = getActivePoFilter(currentStatus, currentPo);

    const isFirstTabLLE = currentStakeholder === "LLE" && currentStatus === "InvoiceReceived";

    const formattedDispatchedFromDate = (isFirstTabLLE && currentDispatchedFromDate)
      ? dayjs(currentDispatchedFromDate).format('YYYY-MM-DD')
      : null;

    const formattedDispatchedToDate = (isFirstTabLLE && currentDispatchedToDate)
      ? dayjs(currentDispatchedToDate).format('YYYY-MM-DD')
      : null;

    getAllInvoicePaymentsWithPaging(
      setDetails,
      0,
      0,
      currentStakeholder,
      currentStatus,
      currentSearch,
      false,
      currentSortField,
      currentSortOrder,
      activePo,
      formattedDispatchedFromDate,
      formattedDispatchedToDate
    );
  };

  const handleOpenGroupModal = (groupId) => {
    if (!groupId) return;
    setSelectedGroupId(groupId);
    setGroupModalOpen(true);
    setGroupLoading(true);
    getInvoicePaymentByGroupId(groupId, (data) => {
      setGroupInvoices(data);
      setGroupLoading(false);
    });
  };

  const handleCloseGroupModal = () => {
    setGroupModalOpen(false);
    setSelectedGroupId('');
    setGroupInvoices([]);
  };

  const handleSortModelChange = async (model) => {
    let sortFieldVal = model[0]?.field;
    if (sortFieldVal === "InvoiceDateStr") sortFieldVal = "InvoiceDate";
    setSortField(sortFieldVal);
    setSortOrder(model[0]?.sort);

    fetchDataWithFilters({ sortField: sortFieldVal, sortOrder: model[0]?.sort });
  };

  const viewPoDetails = (row, fromGroupId = null) => {
    if (!row?.pitPono) return;

    const rawPo = String(row.pitPono).trim().toUpperCase();
    const parts = rawPo.split("-");
    const poPrefix = parts[0];

    const currentStakeholder = row?.stakeholderCatgeory || stakeholderType;

    const navState = {
      selectedRow: row,
      screen: "invoicePayment",
      pageName: "invoicePayment",
      tabValue,
      stakeholderType: currentStakeholder,
      ...(fromGroupId && { openGroupId: fromGroupId })
    };

    if (poPrefix === "SO" || poPrefix === "MSO") {
      navigate('./soDetails', { state: navState });
    } else if (poPrefix === "STO") {
      navigate('./Sto', { state: navState });
    } else if (poPrefix === "SCO") {
      navigate('./Sco', { state: navState });
    } else {
      navigate('./growerPo', { state: navState });
    }
  };

  const viewInvoiceDetails = (row, fromGroupId = null) => {
    const navState = {
      selectedRow: row,
      tabValue,
      pageName: "invoicePayment",
      stakeholderType,
      ...(fromGroupId && { openGroupId: fromGroupId })
    };

    if (row?.stakeholderCatgeory === "Grower") {
      navigate('./growerLleInvoice', { state: navState });
    }
    if (row?.stakeholderCatgeory === "HIT") {
      navigate('./hitInvoice', { state: navState });
    }
    if (row?.stakeholderCatgeory === "LLE") {
      navigate('./lleInvoice', { state: navState });
    }
    if (row?.stakeholderCatgeory === "Transporter") {
      navigate('./trInvoice', { state: navState });
    }
  };

  const viewGrnDetails = (row, fromGroupId = null) => {
    const poType = row.pitGrnSesNumber?.split("-")[0];
    const navState = {
      selectedRow: row,
      tabValue,
      pageName: "invoicePayment",
      stakeholderType,
      ...(fromGroupId && { openGroupId: fromGroupId })
    };

    if (poType === "G") {
      navigate('./GrnFormat', { state: navState });
    }
    if (poType === "SES") {
      navigate('./sesDetailsScreen', { state: navState });
    }
  };

  const viewURNDetails = (urn) => {
    navigate("./viewPageWraper", { state: { urn, tabValue, pageName: "invoicePayment", stakeholderType } });
  };

  const parseDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const isoDate = new Date(value);
    if (!isNaN(isoDate.getTime())) return isoDate;
    const parts = value.split(/[/-]/);
    if (parts.length === 3) {
      const [dd, mm, yyyy] = parts;
      return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    }
    return null;
  };

  const getColumns = (tabVal, currStakeholderType) => {
    const isLLEGroupTab = currStakeholderType === "LLE" && tabVal !== "InvoiceReceived";

    const dynamicCols = [];

    if (isLLEGroupTab) {
      dynamicCols.push({
        field: 'invoicePoGroupNumber',
        headerName: 'Group Reference ID',
        width: 200,
        renderCell: (params) => (
          params.value ? (
            <Link
              component="button"
              variant="body2"
              underline="hover"
              sx={{ fontWeight: 600, color: 'primary.main' }}
              onClick={() => handleOpenGroupModal(params.value)}
            >
              {params.value}
            </Link>
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )
        )
      });
      dynamicCols.push({
        field: 'pitInvoiceNumber',
        headerName: 'Invoice Number',
        width: 150,
      });
      dynamicCols.push({
        field: 'invoiceDate',
        headerName: 'Date of Invoice',
        width: 150,
        type: "date",
        valueGetter: (params) => parseDate(params.row.invoiceDate),
        valueFormatter: (params) => (params.value ? format(params.value, "dd/MM/yyyy") : "")
      });
    } else {
      dynamicCols.push({
        field: 'pitInvoiceNumber',
        headerName: 'Invoice Number',
        width: 180,
        renderCell: (params) => (
          <Link component="button" variant="body2" underline="hover" onClick={() => viewInvoiceDetails(params.row)}>
            {params.value}
          </Link>
        )
      });
    }

    dynamicCols.push({
      field: 'pitPono',
      headerName: 'PO#',
      width: 180,
      renderCell: (params) => (
        <Link component="button" variant="body2" underline="hover" onClick={() => viewPoDetails(params.row)}>
          {params.value}
        </Link>
      )
    });

    if (!isLLEGroupTab) {
      dynamicCols.push({
        field: 'pitGrnSesNumber',
        headerName: 'GRN/SES Number',
        width: 180,
        renderCell: (params) => (
          <Link component="button" variant="body2" underline="hover" onClick={() => viewGrnDetails(params.row)}>
            {params.value}
          </Link>
        )
      });
    }

    dynamicCols.push(
      {
        field: 'urn',
        headerName: 'Stakeholder URN',
        width: 180,
        sortComparator: (a, b) => lastFiveDigitsComparator(a, b),
        renderCell: (params) => (
          <Link component="button" variant="body2" underline="hover" onClick={() => viewURNDetails(params?.row?.urn)}>
            {params.value}
          </Link>
        )
      },
      { field: 'stakeholderName', headerName: 'Stakeholder Name', width: 180 },
      {
        field: 'pitDispatchedQuantity',
        headerName: 'Dispatched Quantity(MT)',
        width: 150,
        type: "number",
        valueGetter: (params) => params.value?.toFixed(4)
      },
      {
        field: 'pitGrnSesQuantity',
        headerName: 'Received Quantity(MT)',
        width: 150,
        type: "number",
        valueGetter: (params) => params.value?.toFixed(4)
      }
    );

    dynamicCols.push(
      {
        field: 'rateAmount',
        headerName: 'Rate(INR)',
        width: 150,
        type: "number",
        valueGetter: (params) => params.value?.toFixed(4)
      },
      {
        field: 'applicableTax',
        headerName: 'Applicable Tax %(if any)',
        width: 120,
        type: "number",
        valueGetter: (params) => params.value?.toFixed(4)
      },
      {
        field: 'cgstTax',
        headerName: 'CGST (INR)',
        width: 150,
        type: "number",
        valueGetter: (params) => params.value?.toFixed(4)
      },
      {
        field: 'sgstTax',
        headerName: 'SGST (INR)',
        width: 150,
        type: "number",
        valueGetter: (params) => params.value?.toFixed(4)
      },
      {
        field: 'igstTax',
        headerName: 'IGST (INR)',
        width: 150,
        type: "number",
        valueGetter: (params) => params.value?.toFixed(4)
      },
      {
        field: 'tdsTax',
        headerName: 'TDS Tax (INR)',
        width: 150,
        type: "number",
        valueGetter: (params) => params.value?.toFixed(4)
      },
      {
        field: 'invoiceTax',
        headerName: 'Invoice Tax (INR)',
        width: 150,
        type: "number",
        valueGetter: (params) => params.value?.toFixed(4)
      },
      {
        field: 'invoiceAmount',
        headerName: 'Invoice Amount(Exc. Taxes) (INR)',
        width: 200,
        type: "number",
        valueGetter: (params) => params.value?.toFixed(4)
      },
      {
        field: 'totalInvoiceAmount',
        headerName: 'Total Invoice Amount (Inc. Taxes) (INR)',
        width: 210,
        type: "number",
        valueGetter: (params) => params.value?.toFixed(4)
      },
      {
        field: 'advancePayment',
        headerName: 'Advance Payment(if any) (INR)',
        width: 150,
        type: "number",
        valueGetter: (params) => params.value?.toFixed(4)
      },
      {
        field: 'netAmountPayable',
        headerName: 'Net Amount Payable (INR)',
        width: 150,
        type: "number",
        valueGetter: (params) => params.value?.toFixed(4)
      }
    );

    if (!isLLEGroupTab) {
      dynamicCols.push(
        {
          field: 'invoiceDate',
          headerName: 'GRN Date/Date of Invoice',
          width: 170,
          type: "date",
          valueGetter: (params) => parseDate(params.row.invoiceDate),
          valueFormatter: (params) => (params.value ? format(params.value, "dd/MM/yyyy") : "")
        },
        {
          field: 'paymentDueDate',
          headerName: 'Payment Due Date',
          width: 150,
          type: "date",
          valueGetter: (params) => parseDate(params.row.paymentDueDate),
          valueFormatter: (params) => (params.value ? format(params.value, "dd/MM/yyyy") : "")
        }
      );
    }

    dynamicCols.push({
      field: 'dispatchedDate', headerName: 'Dispatched Date', width: 150,
      type: "date",
      valueGetter: (params) => parseDate(params.row.dispatchedDate),
      valueFormatter: (params) => (params.value ? format(params.value, "dd/MM/yyyy") : "")
    });

    dynamicCols.push({ field: 'status', headerName: 'Status', width: 180 });

    if (!isLLEGroupTab) {
      dynamicCols.push({ field: 'sapGrnSesNumber', headerName: 'SAP GRN/SES No.', width: 160 });
    }

    if (currStakeholderType === "Transporter") {
      dynamicCols.push({
        field: 'invoiceDocumentList',
        headerName: "Invoice Documents",
        width: 270,
        renderCell: (params) => {
          const docs = params.value || params.row.invoiceDocuments || params.row.uploadedDocumentList || [];
          if (!docs || docs.length === 0) return <Typography variant="caption">No Documents</Typography>;
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5 }}>
              {docs.map((doc, idx) => (
                <Box key={doc.invoiceFileId || doc.blobFileName || idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      viewdownloadDocument(doc?.invoiceFileId || doc?.blobFileName, doc?.invoiceFileName || doc?.fileName, "View");
                    }}
                    underline="hover"
                    sx={{ cursor: 'pointer', fontSize: '0.8rem', textAlign: 'left' }}
                  >
                    {doc.invoiceFileName || doc.fileName}
                  </Link>
                  <Tooltip title="Download">
                    <Button
                      size="small"
                      variant="text"
                      onClick={(e) => {
                        e.stopPropagation();
                        viewdownloadDocument(doc?.invoiceFileId || doc?.blobFileName, doc?.invoiceFileName || doc?.fileName, "Download");
                      }}
                      sx={{ minWidth: 'auto', p: 0, fontSize: '0.7rem', textTransform: 'none' }}
                    >
                      <DownloadIcon />
                    </Button>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          );
        }
      });
    }

    dynamicCols.push(
      { field: 'sapTransId', headerName: 'SAP Trans ID', width: 180 },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        width: 100,
        disableClickEventBubbling: true,
        renderCell: (params) => (
          <>
            <Tooltip title="View">
              <IconButton color="primary" size="small" onClick={() => navigate('invoicePaymentVerify', { state: { type: "View", selectedRow: params?.row, tabVal } })}>
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print">
              <IconButton color="primary" size="small" onClick={() => downloadInvoice(params?.row?.pitInvoiceNumber, params?.row?.stakeholderCatgeory)}>
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )
      }
    );

    return dynamicCols;
  };

  const groupModalColumns = [
    {
      field: 'pitInvoiceNumber',
      headerName: 'Invoice No.',
      width: 180,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          underline="hover"
          sx={{ fontWeight: 600 }}
          onClick={() => {
            viewInvoiceDetails(params.row, selectedGroupId);
          }}
        >
          {params.value || '-'}
        </Link>
      )
    },
    {
      field: 'pitPono',
      headerName: 'PO No.',
      width: 160,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={() => {
            viewPoDetails(params.row, selectedGroupId);
          }}
        >
          {params.value || '-'}
        </Link>
      )
    },
    {
      field: 'pitGrnSesNumber',
      headerName: 'GRN/SES No.',
      width: 160,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={() => {
            viewGrnDetails(params.row, selectedGroupId);
          }}
        >
          {params.value || '-'}
        </Link>
      )
    },
    { field: 'urn', headerName: 'Stakeholder URN', width: 160 },
    {
      field: 'pitDispatchedQuantity',
      headerName: 'Dispatched Quantity (MT)',
      width: 110,
      type: 'number',
      valueGetter: (params) => params.value?.toFixed(4)
    },
    {
      field: 'pitGrnSesQuantity',
      headerName: 'Received Quantity (MT)',
      width: 110,
      type: 'number',
      valueGetter: (params) => params.value?.toFixed(4)
    },
    {
      field: 'rateAmount',
      headerName: 'Rate(INR)',
      width: 150,
      type: "number",
      valueGetter: (params) => params.value?.toFixed(4)
    },
    {
      field: 'cgstTax',
      headerName: 'CGST (INR)',
      width: 150,
      type: "number",
      valueGetter: (params) => params.value?.toFixed(4)
    },
    {
      field: 'sgstTax',
      headerName: 'SGST (INR)',
      width: 150,
      type: "number",
      valueGetter: (params) => params.value?.toFixed(4)
    },
    {
      field: 'igstTax',
      headerName: 'IGST (INR)',
      width: 150,
      type: "number",
      valueGetter: (params) => params.value?.toFixed(4)
    },
    {
      field: 'tdsTax',
      headerName: 'TDS Tax (INR)',
      width: 150,
      type: "number",
      valueGetter: (params) => params.value?.toFixed(4)
    },
    {
      field: 'invoiceTax',
      headerName: 'Invoice Tax (INR)',
      width: 150,
      type: "number",
      valueGetter: (params) => params.value?.toFixed(4)
    },
    {
      field: 'invoiceAmount',
      headerName: 'Invoice Amount(Exc. Taxes) (INR)',
      width: 200,
      type: "number",
      valueGetter: (params) => params.value?.toFixed(4)
    },
    {
      field: 'totalInvoiceAmount',
      headerName: 'Total Invoice Amount (Inc. Taxes) (INR)',
      width: 200,
      type: "number",
      valueGetter: (params) => params.value?.toFixed(4)
    }
  ];

  const groupSummary = React.useMemo(() => {
    return groupInvoices.reduce(
      (acc, curr) => {
        acc.totalQuantity += Number(curr.quantity || curr.pitGrnSesQuantity || 0);
        acc.totalAmount += Number(curr.totalInvoiceAmount || 0);
        acc.netPayable += Number(curr.netAmountPayable || 0);
        return acc;
      },
      { totalQuantity: 0, totalAmount: 0, netPayable: 0 }
    );
  }, [groupInvoices]);

  const selectionTotals = React.useMemo(() => {
    return selectedRows.reduce(
      (acc, row) => {
        const qty = Number(row.quantity ?? row.pitGrnSesQuantity ?? row.pitDispatchedQuantity ?? 0);
        const amt = Number(row.totalInvoiceAmount ?? 0);
        const net = Number(row.netAmountPayable ?? 0);
        acc.totalQuantity += qty;
        acc.totalAmount += amt;
        acc.netPayable += net;
        return acc;
      },
      { totalQuantity: 0, totalAmount: 0, netPayable: 0 }
    );
  }, [selectedRows]);

  const onSendToSAP = () => {
    if (selectedRows?.length === 0) {
      toast.error("Please select at least one row to send to SAP!");
      return;
    }
    setSelectedAction("sendToSAP");
    setActionText("Send to SAP for payment");
    setDialogOpen(true);
  };

  const onApprove = () => {
    if (selectedRows?.length === 0) {
      toast.error("Please select at least one row to approve!");
      return;
    }
    if (stakeholderType === "LLE") {
      if (!approvalInvoiceNumber) {
        toast.error("Please enter Invoice Number before approving for LLE!");
        return;
      }
      if (!approvalInvoiceDate) {
        toast.error("Please select Invoice Date before approving for LLE!");
        return;
      }
    }
    setSelectedAction("approve");
    setActionText("Approve");
    setDialogOpen(true);
  };

  const approveInvoicePaymentRequest = async () => {
    try {
      loaderService.showLoader();
      const formattedInvoiceDate = (stakeholderType === "LLE" && approvalInvoiceDate)
        ? dayjs(approvalInvoiceDate).format('YYYY-MM-DD')
        : null;

      const payload = {
        paymentRequests: selectedRows?.map((item) => ({
          paymentType: "Invoice",
          referenceNo: item?.pitInvoiceNumber,
          stakeholderType: stakeholderType,
          urn: item?.urn,
          ...(stakeholderType === "LLE" && {
            invoiceDate: formattedInvoiceDate,
            invoiceNumber: approvalInvoiceNumber
          })
        })) || [],
        ...(stakeholderType === "LLE" && {
          invoiceDate: formattedInvoiceDate,
          invoiceNumber: approvalInvoiceNumber
        }),
        loginID: getLocalStorageItem('uuid'),
        status: "InvoiceReceivedApproved"
      };

      const response = await apiClient.post(
        UPDATE_PAID_STATUS,
        payload,
        {
          headers: {
            Authorization: 'Bearer ' + getLocalStorageItem('id_token'),
          }
        }
      );

      loaderService.hideLoader();

      if (response?.data?.isSuccess === true) {
        toast.success(response?.data?.message || "Approved successfully");
        fetchDataWithFilters();
      } else {
        toast.error(response?.data?.message || "Failed to approve");
      }
    } catch (error) {
      loaderService.hideLoader();
      toast.error("Failed to approve");
    }
  };

  function onConfirmSendToSAP() {
    setDialogOpen(false);
    if (selectedAction === "approve") {
      approveInvoicePaymentRequest();
      return;
    }

    if (stakeholderType === "LLE" && tabValue === "InvoiceReceivedApproved") {
      sendLLEPaymentToSAP(
        selectedRows,
        callBackSendToSAP,
        "ok",
        setInvoicePaymentTableDetails
      );
    } else {
      const tempArr = selectedRows?.map((item) => ({
        paymentId: item?.id,
        documents: []
      }));
      sendInvoiceToSAPForPayment(
        tempArr,
        callBackSendToSAP,
        "ok",
        setInvoicePaymentTableDetails
      );
    }
  }

  const callBackSendToSAP = () => {
    fetchDataWithFilters();
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      fetchDataWithFilters();
    }
  }, []);

  useEffect(() => {
    if (state?.openGroupId) {
      handleOpenGroupModal(state.openGroupId);
    }
  }, [state?.openGroupId]);

  const [tabValue, setTabValue] = useState(location?.state?.tabValue ? location?.state?.tabValue : "InvoiceReceived");

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
    setStatus(newValue);
    setSelectedRows([]);
    fetchDataWithFilters({ status: newValue });
  };

  const handleStakeholderTypeChange = (e) => {
    const val = e.target.value;
    setStakeholderType(val);
    setSelectedPoNumber("");
    setSelectedRows([]);
    setDispatchedFromDate(null);
    setDispatchedToDate(null);
    setApprovalInvoiceDate(null);
    setApprovalInvoiceNumber("");

    fetchDataWithFilters({
      stakeholderType: val,
      selectedPoNumber: "",
      dispatchedFromDate: null,
      dispatchedToDate: null
    });
  };

  const handlePoNumberChange = (e) => {
    const val = e.target.value;
    setSelectedPoNumber(val);
    setSelectedRows([]);
    fetchDataWithFilters({ selectedPoNumber: val });
  };

  const getManualPoList = () => {
    loaderService.showLoader();
    apiClient.post(GET_PO_LIST,
      {
        "poType": "SCO",
        "approvalStatus": 1,
        "isSapPOAdded": true,
      },
      {
        headers: {
          Authorization: 'Bearer ' + getLocalStorageItem('id_token'),
          "Content-Type": "application/json"
        }
      })
      .then((response) => {
        setManualPoList(response.data?.poDetailsList || []);
        loaderService.hideLoader();
      }).catch((error) => {
        loaderService.hideLoader();
        toast.error(error.message);
      });
  };

  useEffect(() => {
    if (stakeholderType === "LLE") {
      getManualPoList();
    }
  }, [stakeholderType]);

    const gridFunction = (tabVal) => {
    const currentColumns = getColumns(tabVal, stakeholderType);
    const isLLE = stakeholderType === "LLE";
    const isFirstTab = tabVal === "InvoiceReceived";
    const showPoDropdown = isLLE && isFirstTab;
    const showLLEDateFilters = isLLE && isFirstTab;

    return (
      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} mb={2} justifyContent="space-between" alignItems="flex-start">
            <Typography variant="h5" fontWeight={600} sx={{ pt: 0.5 }}>
              Invoice Payment
            </Typography>

            {isFirstTab && (
              <Stack direction="row" spacing={2} alignItems="flex-start">
                {isLLE && (
                  <>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <TextField
                        label="Invoice Number *"
                        value={approvalInvoiceNumber}
                        onChange={(e) => setApprovalInvoiceNumber(e.target.value.slice(0, 16))}
                        size="small"
                        fullWidth
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
                        <Badge bg={`${approvalInvoiceNumber?.length > characterLimit ? 'danger' : 'primary'}`}>
                          {approvalInvoiceNumber?.length}/{characterLimit}
                        </Badge>
                      </Box>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                          label="Invoice Date *"
                          value={approvalInvoiceDate ? dayjs(approvalInvoiceDate) : null}
                          inputFormat="DD/MM/YYYY"
                          onChange={(value) => setApprovalInvoiceDate(value)}
                          renderInput={(params) => (
                            <TextField {...params} size="small" fullWidth />
                          )}
                        />
                      </LocalizationProvider>
                    </FormControl>
                  </>
                )}

                <Button
                  disabled={localStorage.getItem('role') === PAYMENT_ROLES.SCMEndUser || localStorage.getItem('role') === PAYMENT_ROLES.PaymentViewer}
                  onClick={onApprove}
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  sx={{ height: 40 }}
                >
                  Approve
                </Button>
              </Stack>
            )}

            {tabVal === "InvoiceReceivedApproved" && (
              <Button
                disabled={localStorage.getItem('role') === PAYMENT_ROLES.SCMEndUser || localStorage.getItem('role') === PAYMENT_ROLES.PaymentViewer}
                onClick={onSendToSAP}
                variant="contained"
                startIcon={<SendIcon />}
                sx={{ height: 40 }}
              >
                Send to SAP for payment
              </Button>
            )}
          </Stack>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={showLLEDateFilters ? (showPoDropdown ? 3 : 4) : 6}>
              <FormControl fullWidth size="small">
                <InputLabel>Stakeholder Type</InputLabel>
                <Select
                  name='stakeholderType'
                  label="Stakeholder Type"
                  value={stakeholderType}
                  onChange={handleStakeholderTypeChange}
                >
                  <MenuItem value="All">All</MenuItem>
                  {Stakeholder_Type_Mapping.map((item) => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {showPoDropdown && (
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>PO List</InputLabel>
                  <Select
                    name='poNumber'
                    label="PO List"
                    value={selectedPoNumber}
                    onChange={handlePoNumberChange}
                  >
                    <MenuItem value=""><em>All POs</em></MenuItem>
                    {manualPoList.map((po) => (
                      <MenuItem key={po} value={po.poNumber}>{po.poNumber}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {showLLEDateFilters && (
              <>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Dispatched From Date"
                        value={dispatchedFromDate ? dayjs(dispatchedFromDate) : null}
                        inputFormat="DD/MM/YYYY"
                        maxDate={dispatchedToDate ? dayjs(dispatchedToDate) : dayjs()}
                        onChange={(value) => {
                          setDispatchedFromDate(value);
                          setSelectedRows([]);
                          fetchDataWithFilters({ dispatchedFromDate: value });
                        }}
                        renderInput={(params) => (
                          <TextField {...params} size="small" fullWidth />
                        )}
                      />
                    </LocalizationProvider>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Dispatched To Date"
                        value={dispatchedToDate ? dayjs(dispatchedToDate) : null}
                        inputFormat="DD/MM/YYYY"
                        minDate={dispatchedFromDate ? dayjs(dispatchedFromDate) : null}
                        maxDate={dayjs()}
                        onChange={(value) => {
                          setDispatchedToDate(value);
                          setSelectedRows([]);
                          fetchDataWithFilters({ dispatchedToDate: value });
                        }}
                        renderInput={(params) => (
                          <TextField {...params} size="small" fullWidth />
                        )}
                      />
                    </LocalizationProvider>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>

          {isFirstTab && selectedRows.length > 0 && (
            <Paper elevation={0} sx={{ p: 1.5, mb: 2, bgcolor: '#f0f4f8', border: '1px solid #b0bec5', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={2}>
                  <Typography variant="body2" color="textSecondary">
                    Selected Invoices: <strong>{selectedRows.length}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="primary">
                    Total Quantity: <strong>{selectionTotals.totalQuantity.toFixed(4)} MT</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="success.main">
                    Total Amount: <strong>INR {selectionTotals.totalAmount.toFixed(4)}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="success.main">
                    Total Net Amount Payable: <strong>INR {selectionTotals.netPayable.toFixed(4)}</strong>
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          <Box style={{ height: 420, width: '100%' }}>
            {invoicePaymentTableDetails && (
              <DataGrid
                rows={invoicePaymentTableDetails}
                columns={currentColumns}
                onSortModelChange={handleSortModelChange}
                components={{ Toolbar: CustomDataGridToolbar }}
                componentsProps={{
                  toolbar: {
                    rows: invoicePaymentTableDetails,
                    columns: currentColumns,
                    fileName: "InvoicePayments.xlsx",
                    isExportToExcelRequired: true,
                    dateFields: ['invoiceDate', 'paymentDueDate', 'dispatchedDate']
                  }
                }}
                checkboxSelection={(tabVal === "InvoiceReceived" || tabVal === "InvoiceReceivedApproved") && (getLocalStorageItem('role') !== PAYMENT_ROLES?.PaymentViewer)}
                onSelectionModelChange={(ids) => {
                  const selectedIDs = new Set(ids);
                  const selectedRowData = invoicePaymentTableDetails?.filter((row) =>
                    selectedIDs.has(row.id.toString())
                  );
                  setSelectedRows(selectedRowData);
                }}
                isRowSelectable={(params) => {
                  if (tabVal === "InvoiceReceived") {
                    return params.row.status === "InvoiceReceived" && params.row.sapPoNumber && params.row.sapGrnSesNumber;
                  }
                  if (tabVal === "InvoiceReceivedApproved") {
                    return params.row.status === "InvoiceReceivedApproved" && params.row.sapPoNumber && params.row.sapGrnSesNumber && !(params.row.sapTransId);
                  }
                  return false;
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };


  return (
    <>
      {!PROD && <p style={{ textAlign: "right", fontSize: "2vh" }}>PAY05</p>}
      <Box sx={{ width: '100%' }}>
        <TabContext value={tabValue}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList value={tabValue} onChange={handleChange}>
              <Tab label="Invoice Received" value="InvoiceReceived" />
              <Tab label="Ready to send to SAP for payment" value="InvoiceReceivedApproved" />
              <Tab label="Sent to SAP for Payment" value="SentToSAP" />
            </TabList>
          </Box>

          <TabPanel value="InvoiceReceived">{gridFunction("InvoiceReceived")}</TabPanel>
          <TabPanel value="InvoiceReceivedApproved">{gridFunction("InvoiceReceivedApproved")}</TabPanel>
          <TabPanel value="SentToSAP">{gridFunction("SentToSAP")}</TabPanel>
        </TabContext>
      </Box>

      {/* Group Reference Details Modal */}
      <Dialog
        open={groupModalOpen}
        onClose={handleCloseGroupModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, minHeight: 450 }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight={600} component="div" sx={{ lineHeight: 1.2 }}>
                LLE Group References
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Group Reference ID: <strong>{selectedGroupId}</strong>
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleCloseGroupModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5 }}>
          {groupLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 280 }}>
              <CircularProgress size={40} thickness={4} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading invoices...
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Total Invoices
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                      {groupInvoices.length}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Total Quantity
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                      {groupSummary.totalQuantity.toFixed(4)} MT
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Net Payable
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700} color="success.main">
                      INR {groupSummary.netPayable.toFixed(4)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Box style={{ height: 320, width: '100%' }}>
                <DataGrid
                  rows={groupInvoices}
                  columns={groupModalColumns}
                  density="compact"
                  pageSize={5}
                  rowsPerPageOptions={[5, 10]}
                  disableSelectionOnClick
                  sx={{
                    border: '1px solid #e0e0e0',
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f1f5f9',
                      fontWeight: 600
                    }
                  }}
                />
              </Box>
            </Stack>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseGroupModal} variant="outlined" color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationPopover
        onOpen={dialogOpen}
        onClose={handleClose}
        onConfirm={onConfirmSendToSAP}
        action={actionText}
      />
    </>
  );
}

export default InvoicePayment;
